/**
 *
 * Courses
 *
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { useTranslation } from "react-i18next";

import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import { useParams } from "react-router-dom";

import {
  flushState,
  loadCourses,
  updateCourse,
  hardDeleteCourse,
} from "./actions";
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemsListFirstElem,
  makeSelectItemsList,
  makeSelectCourse,
  makeSelectHardDeleteLoading,
} from "./selectors";

// antd component
import { Layout, Form } from "antd";
const { Content } = Layout;

//components
import SideList from "components/SideList";
import Preloader from "components/Preloader";
import RenderPopconfirm from "components/RenderPopconfirm";
// inner components
import CoursesDetail from "./containers/CoursesDetail";

// utils
import { initFormsCourses } from "utils/forms/forms.init";
import { setFormValues } from "utils/forms/forms.utils";
import _local from "utils/localStorageUtils";

export function Courses({
  flushState,
  loading,
  hardDeleteLoading,
  history,
  courseDetail,
  itemsList,
  firstElem,
  loadCourses,
  updateCourse,
  hardDeleteCourse,
  // actions here
}) {
  useInjectReducer({ key: "courses", reducer });
  useInjectSaga({ key: "courses", saga });
  
  const { t, i18n } = useTranslation();

  const newKey = "new";

  const { id: initId } = useParams();

  const localSchool = _local.getLocalSchool();

  const generalForm = useCallback(() => initFormsCourses(localSchool?.subjects), [localSchool?.subjects, i18n?.language]);
  // Form initialization for "general" tab
  const [customForm] = Form.useForm();

  const [currentItem, setCurrentItem] = useState(initId);
  const [currentElement, setCurrentElement] = useState(null);
  const [formHaveChanges, setFormHaveChanges] = useState(false);
  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    !itemsList && loadCourses();
    return () => {
      // Clearing the state after unmounting a component
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // Get first element from lists
  // Because history not trigger update component
  useEffect(() => {
    if (initId) {
      setCurrentItem(initId);
    }
  }, [initId, firstElem]);

  const changeCurrentItem = current => {
    setCurrentItem(current);
    history.push({
      pathname: `/сours/${current}`,
      search: "",
      hash: "",
      state: {},
    });
  };
  const addNewItem = () => {
    setCurrentItem(newKey);
    history.push({
      pathname: `/сours/${newKey}`,
    });
  };

  const resetForm = () => customForm.resetFields();
  const submitForm = () => customForm.submit();
  const setFormFieldsValues = values => {
    values && customForm.setFieldsValue(values);
  };
  // update data in the general form
  const setFormData = (formData, genForm = null) => {
    const values = formData || currentElement;
    const mainForm = genForm || generalForm();
    values && setFormValues(customForm, mainForm, values);
  };

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.courses")}</title>
        <meta name="description" content="" />
      </Helmet>
      {loading && !!!itemsList && <Preloader />}
      {!loading && !!itemsList && (
        <>
          <RenderPopconfirm
            placement="right"
            showPopup={formHaveChanges}
            onConfirm={() => {
              submitForm();
              setFormHaveChanges(false);
            }}
            onCancel={() => {
              setFormHaveChanges(false);
              // update data in the general form
              setFormData();
            }}
          >
            <SideList
              className={formHaveChanges ? "preventClass" : ""}
              type="сours"
              title={t("courses.sideListTitle")}
              // subTitle={`${itemsList?.totalCount || 0} сours`}
              sortProps={["subject.name"]}
              searchProps={[
                "subject.name",
                "subject.abbreviation",
                "responsible.description.firstname",
                "responsible.description.lastname",
                "course_week",
                "students",
                "course_time_start",
                "course_time_end",
                "searchName",
                "course_code",
              ]}
              filterProps={[
                {
                  key: "course_week",
                  label: t("courses.dayOfTheWeek"),
                  group: "week",
                },
                {
                  key: "subject.name",
                  label: t("labels.subject"),
                  group: "subject",
                },
                {
                  key: "level",
                  label: t("labels.level"),
                  group: "level",
                },
              ]}
              list={itemsList?.data}
              currentItem={currentItem}
              currentItemAction={changeCurrentItem}
              addAction={addNewItem}
            />
          </RenderPopconfirm>
          <Content className="main-content-container">
            <CoursesDetail
              form={{
                customForm: customForm,
                resetForm: resetForm,
                submitForm: submitForm,
                setFormData: setFormData,
                setFormFieldsValues: setFormFieldsValues,
              }}
              generalForm={generalForm()}
              currentId={currentItem}
              update={updateCourse}
              updatedItem={courseDetail}
              hardDeleteLoading={hardDeleteLoading}
              hardDeleteItem={hardDeleteCourse}
              formHaveChanges={formHaveChanges}
              setFormHaveChanges={setFormHaveChanges}
              currentElement={currentElement}
              setCurrentElement={setCurrentElement}
            />
          </Content>
        </>
      )}
    </Layout>
  );
}

Courses.defaultProps = {
  loading: false,
};
Courses.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemsList: makeSelectItemsList(),
  firstElem: makeSelectItemsListFirstElem(),
  courseDetail: makeSelectCourse(),
  hardDeleteLoading: makeSelectHardDeleteLoading(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadCourses: () => dispatch(loadCourses()),
    updateCourse: itemData => dispatch(updateCourse(itemData)),
    hardDeleteCourse: itemId => dispatch(hardDeleteCourse(itemId)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Courses);
